import { el } from '@/ui/dom';
import { sfxWelcomeSkid } from '@/audio/sfx';
import { hapticImpact } from '@/ui/haptics';

/**
 * WelcomeBackToast.ts — the post-login "WELCOME BACK, <RACER ID>!" bubble.
 *
 * A gold-rimmed glass chat bubble that races in from the left like a car,
 * skids to a stop (overshoot + rock back, speed streaks trailing it), holds
 * for a beat over the home screen, then speeds off to the right. Fully
 * non-interactive (pointer-events: none) — it never blocks the menu under it.
 * One at a time; reduced motion swaps the drive for a plain fade.
 */

/** How long the bubble sits parked before it drives off (ms). */
const HOLD_MS = 2400;
/** Matches the .welcome-bubble.out animation length in ui.css. */
const EXIT_MS = 450;

export class WelcomeBackToast {
  private readonly layer: HTMLDivElement;
  private timer: number | null = null;

  constructor(parent: HTMLElement) {
    this.layer = el('div', 'welcome-layer', parent);
  }

  /** `anchor` = the title's Racer ID profile block: the bubble parks just
   *  under it with its tail pointing up at the name. Without one it centres. */
  show(name: string, reducedMotion: boolean, anchor?: DOMRect | null): void {
    this.clear();
    const bubble = el('div', 'welcome-bubble', this.layer);
    if (anchor && anchor.width > 0) {
      this.layer.classList.add('anchored');
      this.layer.style.top = `${Math.round(anchor.bottom + 16)}px`;
      this.layer.style.paddingLeft = `${Math.round(anchor.left)}px`;
    } else {
      this.layer.classList.remove('anchored');
      this.layer.style.top = '';
      this.layer.style.paddingLeft = '';
    }
    if (reducedMotion) bubble.classList.add('reduced');
    el('div', 'welcome-kicker', bubble).textContent = 'RACER ID';
    const line = el('div', 'welcome-line', bubble);
    el('span', 'welcome-text', line).textContent = 'WELCOME BACK, ';
    el('span', 'welcome-name', line).textContent = name;
    el('span', 'welcome-text', line).textContent = '!';
    if (!reducedMotion) {
      sfxWelcomeSkid();
      // The buzz lands with the skid, not the launch.
      this.timer = window.setTimeout(() => hapticImpact(), 380);
    }
    window.setTimeout(() => {
      if (bubble.isConnected) this.hide();
    }, HOLD_MS);
  }

  /** Drive the bubble off now (the player navigated away before it left). */
  hide(): void {
    const bubble = this.layer.firstElementChild;
    if (!bubble || bubble.classList.contains('out')) return;
    bubble.classList.add('out');
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.clear(), EXIT_MS);
  }

  private clear(): void {
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = null;
    this.layer.replaceChildren();
  }

  dispose(): void {
    this.clear();
    this.layer.remove();
  }
}
